import { Component, DestroyRef, Inject, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AlertService } from '../../../../../core/services/alert/alert.service';
import { ApiService } from '../../../../../core/services/api/api.service';
import { ToastService } from '../../../../../core/services/toast/toast.service';
import { EnfafModalComponent } from '../../../../../components/enfaf-modal/enfaf-modal.component';
import { EnfafDatetimePickerLightComponent } from '../../../../../components/enfaf-datetime-picker-light/enfaf-datetime-picker-light.component';
import { cloneDeep } from 'lodash-es';
import { DateTime } from 'luxon';
import { firstValueFrom } from 'rxjs';
import { apiErrorMessage } from '../../load-error';

/** `type` of the modal data when `orderId` is a purchase id (god orders list); any other type goes through the legacy endpoint. */
export const PURCHASE_DATE_TYPE = 'purchase';

/** First day `PUT /god/orders/purchases/:id/date` accepts; the last one is today + 1 year (D77). */
export const PURCHASE_DATE_MIN = '2015-01-01';
const PURCHASE_DATE_MAX_YEARS_AHEAD = 1;
/** 400 of the purchase endpoint for a day out of range: `date must be between 2015-01-01 and 2027-10-23`. */
/** Errors of the picker's input (`NgxMatDatepickerInput`) for a day before `min` / after `max`. */
const RANGE_ERRORS = ['matDatetimePickerMin', 'matDatetimePickerMax'];
const OUT_OF_RANGE_MESSAGE = /date must be between (\d{4}-\d{2}-\d{2}) and (\d{4}-\d{2}-\d{2})/;

// The adapter is Luxon's: `dateA11yLabel` is the name of each day of the calendar. Moment's 'LL' is the two-digit month
// in Luxon, so every day was called "09" (ENF-2464 F-6); 'DDD' is the full localized date ("28 de octubre de 2026").
const dateFormats = (dateInput: string) => ({
  parse: { dateInput },
  display: { dateInput, monthYearLabel: 'MMM yyyy', dateA11yLabel: 'DDD', monthYearA11yLabel: 'MMMM yyyy' }
});

@Component({
  selector: 'app-modal-change-order-date',
  imports: [ReactiveFormsModule, TranslateModule, EnfafModalComponent, EnfafDatetimePickerLightComponent],
  providers: [
    // A purchase only has a day, so its field shows no time (the app-wide format is "dd/MM/yyyy HH:mm").
    {
      provide: MAT_DATE_FORMATS,
      deps: [MAT_DIALOG_DATA],
      useFactory: (data: any) => dateFormats(data?.type === PURCHASE_DATE_TYPE ? 'dd/MM/yyyy' : 'dd/MM/yyyy HH:mm')
    }
  ],
  templateUrl: './modal-change-order-date.component.html',
  styleUrl: './modal-change-order-date.component.scss'
})
export class ModalChangeOrderDateComponent implements OnInit {
  showCloseAlert = false;
  changeForm!: FormGroup;
  isChangingDate = false;
  /** A purchase only has a day; the legacy pre-order endpoint keeps the time of day. */
  hideTime = false;
  subtitle = '';
  /** Range the picker offers for a purchase (the API's); none for the legacy pre-order endpoint. */
  minDate: Date | null = null;
  maxDate: Date | null = null;
  /** Why the API refused the date (out of range, after the next payment): shown in the modal, which stays open. */
  apiError: string | null = null;
  private initialDate: DateTime | null = null;

  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<ModalChangeOrderDateComponent>,
    private alert: AlertService,
    private api: ApiService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.initForm();
    // The dialog keeps `disableClose` (a click on the backdrop must not drop a picked date), so Esc is handled here:
    // the same path as the X, with the confirmation when there are unsaved changes. Only this modal.
    this.dialogRef
      .keydownEvents?.()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event.key !== 'Escape' || this.isChangingDate || this.askingToClose) return;
        event.preventDefault();
        this.askingToClose = true;
        void this.confirmCloseModal().finally(() => (this.askingToClose = false));
      });
  }

  /** The «close without saving» confirmation is open: a second Esc must not stack another one. */
  private askingToClose = false;

  get dateControl(): FormControl<Date | null> {
    return this.changeForm.get('date') as FormControl<Date | null>;
  }

  /** Message under the field: out of the offered range, or missing / not a date. */
  get fieldErrorLabel(): string {
    const control = this.dateControl;
    if (this.minDate && this.maxDate && RANGE_ERRORS.some((error) => control.hasError(error))) {
      return this.outOfRangeMessage(DateTime.fromJSDate(this.minDate), DateTime.fromJSDate(this.maxDate));
    }
    return this.translate.instant('GOD_ORDERS.EDIT_DATE.REQUIRED');
  }

  /** True while the picked date equals the one the modal opened with (nothing to save). */
  get isUnchanged(): boolean {
    const value = toDateTime(this.dateControl.value);
    if (!value || !this.initialDate) return false;
    const unit = this.hideTime ? 'day' : 'minute';
    return value.hasSame(this.initialDate, unit);
  }

  async confirmCloseModal(): Promise<any> {
    this.showCloseAlert = this.changeForm?.dirty ?? false;
    if (this.showCloseAlert) {
      const closeModal = await this.alert.presentCloseWindowAlert();
      return closeModal ? this.closeModal() : null;
    }

    this.closeModal();
  }

  closeModal(result?: any): void {
    this.dialogRef.close(result);
  }

  initForm(): void {
    const receivedData = this.data ? cloneDeep(this.data) : null;
    const { type, orderId, date, label } = receivedData ?? {};
    if (!type || !orderId || !date) {
      this.closeModal();
      return;
    }

    this.hideTime = type === PURCHASE_DATE_TYPE;
    if (this.hideTime) {
      const today = DateTime.local().startOf('day');
      this.minDate = DateTime.fromISO(PURCHASE_DATE_MIN).toJSDate();
      this.maxDate = today.plus({ years: PURCHASE_DATE_MAX_YEARS_AHEAD }).toJSDate();
    }
    // An order ("#341", or the id when there is no label) reads "Pedido #341"; a product name (pending transfers and
    // financing requests, purchases without number) is shown on its own: "Pedido Pack Curso…" reads oddly.
    const order = label ?? '#' + orderId;
    this.subtitle = String(order).startsWith('#') ? this.translate.instant('GOD_ORDERS.EDIT_DATE.SUBTITLE', { order }) : String(order);
    this.initialDate = DateTime.fromISO(date);
    this.changeForm = new FormGroup({
      type: new FormControl(type, Validators.required),
      orderId: new FormControl(orderId, Validators.required),
      date: new FormControl<Date | null>(this.initialDate.toJSDate(), Validators.required)
    });
    // A new date may be accepted: the refusal of the previous one no longer applies.
    this.dateControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => (this.apiError = null));
  }

  async changeOrderDate(): Promise<void> {
    if (this.changeForm.invalid || this.isChangingDate || !toDateTime(this.dateControl.value)) return;
    try {
      this.isChangingDate = true;
      this.apiError = null;
      const { type, orderId, date } = this.changeForm.value as { type: string; orderId: string | number; date: Date };
      const picked = toDateTime(date);
      if (!picked) return;
      // A purchase edits its first payment through the god-orders endpoint (D46), which answers with the recalculated row.
      if (type === PURCHASE_DATE_TYPE) {
        const purchase = await firstValueFrom(this.api.changeGodPurchaseDate(String(orderId), picked.toFormat('yyyy-MM-dd')));
        this.isChangingDate = false;
        this.toast.presentSuccessToast(this.translate.instant('GOD_ORDERS.EDIT_DATE.SUCCESS'));
        this.closeModal({ dateUpdated: purchase.startedAt, purchase });
        return;
      }
      const body = { type, orderId, date: picked.toUTC().toISO() };
      const response = await firstValueFrom(this.api.changeOrderDate(body));
      this.isChangingDate = false;
      this.toast.presentSuccessToast(response.message);
      this.closeModal({ dateUpdated: body.date });
    } catch (errorResponse: any) {
      this.isChangingDate = false;
      const refusal = this.changeForm.value.type === PURCHASE_DATE_TYPE ? this.purchaseDateRefusal(errorResponse) : null;
      if (refusal) {
        this.apiError = refusal;
        return;
      }
      this.toast.presentErrorToast(apiErrorMessage(errorResponse, this.translate.instant('GOD_ORDERS.EDIT_DATE.ERROR')));
    }
  }

  /**
   * The refusals of the purchase endpoint the admin can fix by picking another day (D72, D77), translated. Null for
   * anything else (not found, changed meanwhile, network…), which keeps the generic toast.
   */
  private purchaseDateRefusal(errorResponse: any): string | null {
    const status = errorResponse?.status;
    const body = errorResponse?.error;
    if (status === 409 && body?.code === 'DATE_AFTER_NEXT_PAYMENT') {
      return this.translate.instant('GOD_ORDERS.EDIT_DATE.AFTER_NEXT_PAYMENT');
    }
    if (status !== 400) return null;
    const range = typeof body?.message === 'string' ? OUT_OF_RANGE_MESSAGE.exec(body.message) : null;
    if (range) return this.outOfRangeMessage(DateTime.fromISO(range[1]), DateTime.fromISO(range[2]));
    return this.translate.instant('GOD_ORDERS.EDIT_DATE.INVALID');
  }

  private outOfRangeMessage(min: DateTime, max: DateTime): string {
    return this.translate.instant('GOD_ORDERS.EDIT_DATE.OUT_OF_RANGE', { min: min.toFormat('dd/MM/yyyy'), max: max.toFormat('dd/MM/yyyy') });
  }
}

/**
 * The picker writes through the app's `DateAdapter` (Luxon: a `DateTime`), while the initial value and a typed date
 * are a native `Date`. Both are read the same way; anything else (or an invalid date) is null.
 */
function toDateTime(value: unknown): DateTime | null {
  const parsed = DateTime.isDateTime(value) ? value : value instanceof Date ? DateTime.fromJSDate(value) : null;
  return parsed?.isValid ? parsed : null;
}
